/**
 * KioskAccessibilityToggle - Floating accessibility mode toggle button
 * Shows on all Kiosk screens for easy access
 */
import { Eye, EyeOff, Type, Contrast } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';

export default function KioskAccessibilityToggle({
  accessibilityEnabled,
  setAccessibilityEnabled,
  isAccessible
}) {
  const [showPanel, setShowPanel] = useState(false);

  return (
    <div className="fixed bottom-4 right-4 z-50" data-testid="kiosk-accessibility-toggle">
      {/* Toggle Button */}
      <Button
        onClick={() => setShowPanel(!showPanel)}
        className={`rounded-full shadow-xl ${
          accessibilityEnabled 
            ? 'bg-primary text-white' 
            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
        } ${isAccessible ? 'w-20 h-20' : 'w-16 h-16'}`}
        data-testid="kiosk-accessibility-btn"
      >
        {accessibilityEnabled ? (
          <Eye className={`${isAccessible ? 'w-10 h-10' : 'w-8 h-8'}`} />
        ) : (
          <EyeOff className={`${isAccessible ? 'w-10 h-10' : 'w-8 h-8'}`} />
        )}
      </Button>

      {/* Expanded Panel */}
      {showPanel && (
        <div className={`absolute bottom-20 right-0 bg-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-700 ${isAccessible ? 'w-80' : 'w-72'}`}>
          <h3 className={`${isAccessible ? 'text-2xl' : 'text-xl'} font-bold text-white mb-4 flex items-center gap-2`}>
            <Eye className="w-6 h-6 text-primary" />
            Accessibility
          </h3>

          {/* Large Text Toggle */}
          <button
            onClick={() => setAccessibilityEnabled(!accessibilityEnabled)}
            className={`w-full p-4 rounded-xl mb-3 flex items-center justify-between transition-colors ${
              accessibilityEnabled 
                ? 'bg-primary/20 border border-primary text-white' 
                : 'bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700'
            }`}
            data-testid="kiosk-accessibility-large-text"
          >
            <div className="flex items-center gap-3">
              <Type className={`${isAccessible ? 'w-8 h-8' : 'w-6 h-6'}`} />
              <span className={`${isAccessible ? 'text-xl' : 'text-lg'} font-medium`}>
                Large Text & Buttons
              </span>
            </div>
            <div className={`w-12 h-7 rounded-full transition-colors ${
              accessibilityEnabled ? 'bg-primary' : 'bg-gray-600'
            }`}>
              <div className={`w-5 h-5 bg-white rounded-full m-1 transition-transform ${
                accessibilityEnabled ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </div>
          </button>

          {/* High Contrast Info */}
          <div className="text-gray-400 text-sm mt-4">
            <p>Accessibility mode enables:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Larger text and buttons</li>
              <li>Increased touch targets</li>
              <li>Higher contrast colors</li>
              <li>Screen reader support</li>
            </ul>
          </div>

          {/* Close Button */}
          <Button
            variant="ghost"
            onClick={() => setShowPanel(false)}
            className={`w-full mt-4 ${isAccessible ? 'py-4 text-lg' : 'py-3'} text-gray-400 hover:text-white hover:bg-gray-800 rounded-xl`}
          >
            Close
          </Button>
        </div>
      )}

      {/* Simple Indicator when enabled */}
      {accessibilityEnabled && !showPanel && (
        <div className="absolute -top-2 -left-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
          <span className="text-white text-xs font-bold">A+</span>
        </div>
      )}
    </div>
  );
}
